import { Box, Typography } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { useHistory } from "react-router-dom";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  adhocKycErrorMessage,
  useAdhocKycRetryMutation,
} from "src/store/features/api/adhocKyc";
import {
  AdhocKycBody,
  AdhocKycError,
  AdhocKycHeading,
  AdhocKycMatchNotice,
  AdhocKycSpinner,
  ADHOC_KYC_MINT,
  ADHOC_KYC_NAVY,
} from "./AdhocKycShell";
import { issuerMismatchSentence, retryInstruction } from "./priorAttempt";

export interface DoneStepProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
  /** Where "Go to Caleb & Brown" lands — the portfolio when logged in, the login page otherwise. */
  homePath: string;
  /** True while the router is still polling for the DVS verdict: show "checking", not the tick. */
  checking?: boolean;
}

export const ADHOC_KYC_CHECKING_MESSAGE = "Checking your details with the document issuer";

/**
 * Status card rows. The verification row deliberately collapses the DVS
 * outcome to Running / Complete — match vs no-match is an ops signal, never
 * something we tell the customer. (The one exception is the retry offer
 * below, which names the rejected element so the customer can fix it.)
 */
export const doneRows = (context: AdhocKycContextResponse): Array<[string, string]> => [
  ["Consent", context.consentRecorded ? "Recorded" : "Pending"],
  [
    "Details",
    context.detailsOutcome === "updated"
      ? "Updated"
      : context.detailsOutcome === "confirmed"
      ? "Confirmed"
      : "Pending",
  ],
  ["Questions", context.questionsSubmitted ? "Submitted" : "Pending"],
  [
    "Verification",
    context.verificationStatus && context.verificationStatus !== "pending" ? "Complete" : "Running",
  ],
];

/**
 * The last screen. Three shapes: still waiting for the issuer (spinner, the
 * router polls); the issuer could not match something and one retry remains
 * (name it, offer "Check and resubmit" in this same session — no new email,
 * no new consent); or done.
 */
export default function DoneStep({
  auth,
  context,
  homePath,
  checking,
}: DoneStepProps): JSX.Element {
  const history = useHistory();
  const [retry, { isLoading, error }] = useAdhocKycRetryMutation();

  if (checking) {
    return (
      <Box data-testid="adhoc-kyc-done-checking" sx={{ textAlign: "center" }}>
        <AdhocKycSpinner message={ADHOC_KYC_CHECKING_MESSAGE} />
        <AdhocKycBody>This usually takes under a minute. Please keep this page open.</AdhocKycBody>
      </Box>
    );
  }

  if (context.retry) {
    return (
      <Box data-testid="adhoc-kyc-done-retry-offer">
        <AdhocKycHeading>One more check is needed</AdhocKycHeading>
        <AdhocKycBody>
          <span data-testid="adhoc-kyc-issuer-mismatch">
            {issuerMismatchSentence(context.retry.failedFields)}
          </span>{" "}
          {retryInstruction(context.retry)}
        </AdhocKycBody>
        <AdhocKycMatchNotice />
        {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
        <NextButton
          fullWidth
          disabled={isLoading}
          onClick={() => retry({ auth })}
          data-testid="adhoc-kyc-done-retry"
        >
          Check and resubmit
        </NextButton>
        <BackButton
          fullWidth
          sx={{ mt: 1.5 }}
          onClick={() => history.push(homePath)}
          data-testid="adhoc-kyc-done-home"
        >
          Not now — go to Caleb &amp; Brown
        </BackButton>
      </Box>
    );
  }

  return (
    <Box data-testid="adhoc-kyc-done-step" sx={{ textAlign: "center" }}>
      <CheckCircleOutline sx={{ fontSize: 56, color: ADHOC_KYC_MINT, mb: 1 }} />
      <AdhocKycHeading>Thanks — you&rsquo;re all done</AdhocKycHeading>
      <AdhocKycBody>
        There&rsquo;s nothing more you need to do. If we need anything else our team will be in
        touch.
      </AdhocKycBody>
      <Box
        component="dl"
        data-testid="adhoc-kyc-status-card"
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          bgcolor: "#FFFFFF",
          p: 2,
          m: 0,
          mb: 3,
          textAlign: "left",
        }}
      >
        {doneRows(context).map(([label, value]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
            <Typography component="dt" sx={{ fontSize: "14px", color: "#6B7280" }}>
              {label}
            </Typography>
            <Typography
              component="dd"
              sx={{ fontSize: "14px", fontWeight: 600, color: ADHOC_KYC_NAVY, m: 0 }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      <NextButton
        fullWidth
        onClick={() => history.push(homePath)}
        data-testid="adhoc-kyc-done-home"
      >
        Go to Caleb &amp; Brown
      </NextButton>
    </Box>
  );
}
